import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SCHEMA_FAMILIES } from './schemaFamilies';
import { LandingAuthButton, LandingFooter, LandingHeader, SignedInNotice, useInquiryForm } from './LandingShell';
import './landing.css';

const FamiliesLanding = () => {
  const form = useInquiryForm(SCHEMA_FAMILIES, { formId: 'enrollForm', submitId: 'submitBtn' });

  useEffect(() => {
    document.title = 'Acuity Learn — Pre-register your child';
    return () => {
      document.title = 'User Information System';
    };
  }, []);

  return (
    <div className="onboarding-page">
      <LandingHeader logoTo="/onboarding" />

      <main id="top">
        <section className="hero">
          <div className="wrap">
            <span className="eyebrow">Now accepting pre-registrations</span>
            <h1>Pre-register your child for a private school or homeschool program.</h1>
            <p className="lede">
              Acuity Learn supports families who want a private school education or a structured homeschool
              program — with real coursework, teacher support, and a record of everything your child completes.
              Tell us a little about your family and we&apos;ll follow up with next steps.
            </p>
            <div className="hero-actions">
              <a className="btn btn-primary" href="#enroll">Pre-Registration</a>
              <LandingAuthButton />
            </div>
            <p className="hero-note">Takes about two minutes. You&apos;ll need each child&apos;s name and the grade they&apos;re entering.</p>
            <p className="switcher">
              <span>Are you a school or district?</span>
              <Link to="/onboarding">Schools and districts start here →</Link>
            </p>
          </div>
        </section>

        <section className="capabilities" aria-label="What families get">
          <div className="wrap">
            <div className="cap-grid">
              <div className="cap">
                <div className="dot" style={{ background: 'rgba(15,110,86,.1)' }}>
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#0F6E56" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                </div>
                <h3>Real coursework</h3>
                <p>Standards-aligned lessons across core subjects, built by educators and paced for your child.</p>
              </div>
              <div className="cap">
                <div className="dot" style={{ background: 'rgba(43,113,59,.1)' }}>
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#2B713B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                </div>
                <h3>Teacher support</h3>
                <p>Work is reviewed and given feedback by real teachers, not scored by a machine alone.</p>
              </div>
              <div className="cap">
                <div className="dot" style={{ background: 'rgba(128,90,18,.1)' }}>
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#805A12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                </div>
                <h3>A record that follows them</h3>
                <p>Attendance, progress, and completed work are kept as an official record you can share.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="form-section" id="enroll">
          <div className="wrap">
            <div className="form-card">
              <div className="form-head" ref={form.formHeadRef}>
                <h2>Pre-registration</h2>
                <p>
                  Tell us how to reach you and what you&apos;re looking for. Our team will review your details and follow
                  up by email with next steps. Fields marked with an asterisk are required.
                </p>
                <SignedInNotice />
              </div>

              <div className="form-error" ref={form.errorRef} role="alert" />

              <form className="form-body" id={form.formId} ref={form.formRef} noValidate />

              <div className="form-foot" ref={form.formFootRef}>
                <p>We use these details only to follow up about pre-registration. We ask for each child&apos;s name and grade so we can prepare the right placement — nothing further about your child is needed at this stage.</p>
                <button className="btn btn-primary btn-submit" type="submit" form={form.formId} id={form.submitId} ref={form.submitRef}>
                  Submit pre-registration
                </button>
              </div>

              <div className="confirm" ref={form.confirmRef} role="status" aria-live="polite" tabIndex={-1}>
                <div className="mark">
                  <svg width="27" height="27" viewBox="0 0 24 24" fill="none" stroke="#2B713B" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <h2>Thank you — we&apos;ve got your pre-registration.</h2>
                <p ref={form.confirmNameRef}>Our team will review what you&apos;ve shared and follow up by email with next steps.</p>
                <p>If there&apos;s anything we need from you before then, we&apos;ll ask in that email.</p>
                <p className="ref" ref={form.confirmRefEl} />
              </div>
            </div>
          </div>
        </section>
      </main>

      <LandingFooter note="Acuity Learn is a K-12 student information and curriculum platform. Details submitted here are used only to follow up about pre-registration." />
    </div>
  );
};

export default FamiliesLanding;
